// 「고객 지원을 위한 접근 허용」 동의 카드·첫 로그인 고르기 창·직원 목록 지원 계정 행 규칙(계약 SUPPORT 2·3·9). 1년 재확인 없음(10-31).
// 동의 문구·판 번호는 서버(GET /support/consent 의 text:{version, title, summary, body})만 그린다 — 화면 상수는 서버 값이 없을 때만(CI-21, DD-16).
// admin-hospital·admin-manufacturer 같은 내용. Vue·axios 를 들이지 않는 순수 모듈(tests/supportConsent.test.mjs).

// 이름·설명: 서버 text.title·text.summary 가 없을 때만(CONSENT-1028 1번)
export const CONSENT_TITLE = "고객 지원을 위한 접근 허용";
export const CONSENT_SUMMARY =
  "고객 지원을 위해 서티라이프·담당 리셀러 담당자가 우리 회사 화면에 접근하는 것을 허용하거나 끕니다.";
export const CONSENT_OFF_TEXT =
  "허용 안 함 — 서티라이프·담당 리셀러 고객지원이 이 회사 화면에 들어올 수 없습니다.";
export const CONSENT_OFF_DONE_TEXT =
  "지원 계정이 바로 잠기고, 열린 지원 접속은 1분 안에 끝납니다.";
export const CONSENT_OFF_CONFIRM_TEXT =
  "고객 지원을 위한 접근 허용을 끕니다. 지원 계정이 바로 잠기고, 열린 지원 접속은 1분 안에 끝납니다.";
export const SUPPORT_ACCOUNT_NOTE =
  "지원 계정: 서티라이프 지원 계정 · 편집 불가 (첫 지원 때 생김)";
// 503 AUDIT_UNAVAILABLE = 기록 실패 → 동의 변경도 실패(계약 SUPPORT 2). 까닭은 axios 공통 알림이 이미 띄움
export const CONSENT_NOT_CHANGED_TEXT = "허용 상태가 바뀌지 않았습니다.";
export const CONSENT_ON_DONE_TEXT = "고객 지원을 위한 접근을 허용했습니다.";

/** ISO·숫자 시각 → KST "YYYY-MM-DD", 읽을 수 없으면 "" */
export const kstDate = (value) => {
  if (value == null || value === "") return "";
  const t = typeof value === "number" ? value : Date.parse(value);
  if (!Number.isFinite(t)) return "";
  return new Date(t + 9 * 3600 * 1000).toISOString().slice(0, 10);
};

const personName = (by) => (typeof by === "string" ? by : by?.name || "");

/**
 * 카드는 회사 관리자에게만, 지원 탭에서는 늘 숨김(서브 계정은 동의를 만들거나 늘리지 못함 — 서버도 403 SUPPORT_SCOPE_DENIED).
 * perms 를 넘기면(병원·제조사) 서버가 모드와 상관없이 늘 보는 동의 보기 권한(support.consent.view)도 있어야 함 — 「계정 관리」 직원은
 * 관리자 표시만 있고 이 권한이 없어 화면마다 403 알림이 떴음(10-30). null = 아직 못 읽음 → 숨김.
 * perms 를 넘기지 않으면(undefined) 관리자 표시만 봄(옛 규칙).
 */
export const consentCardVisible = ({ isCompanyManager, supportTab, perms }) =>
  isCompanyManager === true &&
  !supportTab &&
  (perms === undefined || (Array.isArray(perms) && perms.includes("support.consent.view")));

/**
 * 첫 로그인 「허용 / 허용 안 함」 고르기 창(CONSENT-1028 5번): 카드를 볼 수 있는 사람(회사 관리자·지원 탭 아님) + 서버 needsChoice.
 * 403 NOT_COMPANY_MANAGER·SUPPORT_SCOPE_DENIED 로 동의를 못 읽으면 consent=null → 띄우지 않음.
 */
export const consentChoiceNeeded = (consent, ctx) =>
  consentCardVisible(ctx) && consent?.needsChoice === true;

/** 켠 쪽 한 줄(CONSENT-1028 표). 있는 값만 — 모르면 "" */
export const consentByText = (consent) => {
  if (!consent) return "";
  if (consent.status !== "ON") {
    if (!consent.revokedAt) return "";
    const who = personName(consent.revokedBy);
    if (!who) return "관리자가 껐어요";
    // 서티라이프·리셀러가 끈 것(이름 = 회사 이름)은 「○○에서」
    return consent.revokeSource && consent.revokeSource !== "IN_APP" ? `${who}에서 껐어요` : `${who} 님이 껐어요`;
  }
  const who = personName(consent.consentedBy);
  if (consent.source === "EXISTING") return "기존 가입 회사라 기본으로 켜져 있어요";
  if (consent.source === "IN_APP") return who ? `${who} 님이 켰어요` : "관리자가 켰어요";
  if (consent.source === "OPERATOR") return `${who || "서티라이프"}에서 유지보수 계약으로 켰어요`;
  if (consent.source === "RESELLER") return `${who || "리셀러"}에서 유지보수 계약으로 켰어요`;
  if (consent.source === "OFFLINE") return "서면 동의로 켜졌어요";
  return "";
};

/**
 * 동의 상태 → 카드 한 줄·버튼.
 * @returns {{title, summary, on, text, byText, offline, offlineText, action: 'ON'|'OFF'}}  action = 누를 수 있는 버튼(ON 허용하기 · OFF 허용 끄기)
 */
export function consentView(consent) {
  const head = {
    title: consent?.text?.title || CONSENT_TITLE,
    summary: consent?.text?.summary || CONSENT_SUMMARY,
    byText: consentByText(consent),
  };
  if (consent?.status !== "ON")
    return { ...head, on: false, text: CONSENT_OFF_TEXT, offline: false, offlineText: "", action: "ON" };
  const offline = consent.source === "OFFLINE";
  const when = kstDate(consent.consentedAt);
  // 누가 켰는지는 byText 한 줄로(운영·리셀러가 켠 것도 있어 「(앱에서)」로 묶지 않음)
  const detail = [
    when,
    consent.textVersion && `문구 ${consent.textVersion}`,
  ].filter(Boolean).join(" · ");
  return {
    ...head,
    on: true,
    text: detail ? `허용함 — ${detail}` : "허용함",
    offline,
    // 서면 동의 알림(3-2): 운영팀이 등록한 동의는 카드에 늘 보임
    offlineText: offline
      ? `서티라이프 운영팀이 서면 동의를 등록했습니다${when ? `(${when})` : ""}. 동의한 적이 없다면 [허용 끄기]를 누르세요.`
      : "",
    action: "OFF",
  };
}

/**
 * PUT /support/consent 본문 {status, textVersion}. 켤 때는 지금 보여 준 문구의 판(text.version)이 있어야 함 — 없으면 null.
 */
export const consentPutBody = (status, consent) => {
  const textVersion = consent?.text?.version || consent?.textVersion || null;
  if (status === "ON" && !consent?.text?.version) return null;
  return { status, textVersion };
};

/** 동의 변경 실패 문구(axios 공통 알림이 이미 띄운 코드면 짧게) */
export const consentErrorText = (error) => {
  const code = error?.response?.data?.code;
  if (code === "AUDIT_UNAVAILABLE" || code === "SUPPORT_SCOPE_DENIED") return CONSENT_NOT_CHANGED_TEXT;
  const msg = error?.response?.data?.message;
  return typeof msg === "string" && msg ? msg : "허용 상태를 바꾸지 못했습니다. 잠시 후 다시 시도해 주세요.";
};

/**
 * 직원 목록 줄의 지원 계정 표시(계약 SUPPORT 3 — 목록 응답 줄 support:{kind, label}). 지원 계정이 아니면 null.
 * 서버 label 을 그대로 쓰고 「편집 불가」를 붙임. 판정은 support 칸으로만(아이디 접두어로 정하지 않음).
 */
export const supportRowLabel = (row) => {
  const s = row?.support;
  if (!s || typeof s !== "object") return null;
  const base = s.label || (s.kind === "RESELLER" ? "리셀러 지원 계정" : "서티라이프 지원 계정");
  return base.includes("편집 불가") ? base : `${base} · 편집 불가`;
};

/**
 * 서버 동의 문구(text.body)를 줄마다 나눔(11-02) — 「이름: 내용」 줄은 {label, text} 항목(화면이 이름을 굵게 강조), 나머지는 label 없는 안내 문단.
 * ponytail: 이름은 12자까지만 항목으로 봄 — 문장 속 「:」를 항목으로 잘못 읽지 않게
 */
export const consentBodyLines = (body) =>
  String(body || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const m = /^([^:]{1,12}):\s*(.+)$/.exec(line);
      return m ? { label: m[1], text: m[2] } : { label: "", text: line };
    });
